import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/react';
import { gsap, ScrollTrigger } from 'gsap/all';
import { fullHeight, useHsHeader } from '../../util/hsHeader';
import AudioPlayer, { AudioPlayerMobile } from '../AudioPlayer';
import { useDeviceSize } from '../../util/useDeviceSize';
import { useStory } from '../StoryContent';
import {
  centered,
  AnimatedTextWrapper,
  MobileBlockWrapper,
} from '../../styles/helpers';
import { P } from '../../styles/typography';
import { FadeInAnimation } from '../../effects/withAnimation';

gsap.registerPlugin(ScrollTrigger);

export default function AudioSlide6({ audio, copy }) {
  const [{ headerHeight }] = useHsHeader();
  const { toggleVideo } = useStory();
  const { isWideScreen } = useDeviceSize();
  const [isAudioFocused, setIsAudioFocused] = useState(false);

  const slideTriggerRef = useRef();
  const copyRef = useRef();
  const audioRef = useRef();

  useEffect(() => {
    if (!isWideScreen) {
      return undefined;
    }

    const tl = gsap
      .timeline()
      .to(copyRef.current, {
        y: 0,
        duration: 0.2,
        ease: 'ease-in',
      })
      .to(audioRef.current, {
        css: {
          pointerEvents: 'auto',
          opacity: 1,
        },
        duration: 0,
        delay: 0.1,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(slideTriggerRef.current, {
        duration: 0.4,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(slideTriggerRef.current, {
        duration: 0.01,
      });

    const st = ScrollTrigger.create({
      animation: tl,
      trigger: slideTriggerRef.current,
      pin: true,
      pinSpacing: false,
      scrub: true,
      start: 'top top',
      end: `bottom 0`,
      anticipatePin: 1,
    });

    return () => {
      tl.kill();
      st.kill();
    };
  }, [headerHeight, toggleVideo, isWideScreen]);

  if (isWideScreen) {
    return (
      <div
        css={css`
          ${fullHeight(3)};
          position: relative;
          z-index: 2;
          transform: translateY(-100vh);
        `}
        ref={slideTriggerRef}
      >
        <div
          css={css`
            width: 100%;
            ${fullHeight()};
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;
          `}
        >
          <div
            css={css`
              ${centered};
              width: 50%;
              height: 50%;
              opacity: 0;
              transition: opacity 0.2s ease-in;
              pointer-events: none;
            `}
            ref={audioRef}
          >
            <AudioPlayer url={audio.url} isFocused={isAudioFocused} />
          </div>
        </div>

        <div
          css={css`
            ${fullHeight()};
            width: 100%;
            left: 0;
            top: 0;
            position: absolute;
            display: flex;
            align-items: center;
            transform: translateY(100%);
            pointer-events: none;
          `}
          ref={copyRef}
        >
          <P
            css={css`
              max-width: 670px;
              width: 50%;
              transform: translateX(10%);
            `}
          >
            {copy[0].text}
          </P>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AnimatedTextWrapper>
        <P mobile>{copy[0].text}</P>
      </AnimatedTextWrapper>

      <MobileBlockWrapper
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <FadeInAnimation>
          <AudioPlayerMobile url={audio.url} />
        </FadeInAnimation>
      </MobileBlockWrapper>
    </div>
  );
}

AudioSlide6.propTypes = {
  audio: PropTypes.shape({ url: PropTypes.string }),
  copy: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.node })),
};

AudioSlide6.defaultProps = {
  audio: null,
  copy: null,
};
